import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api, { isAuthenticated } from '../utils/api';

const COLORS = ['#2A0000', '#E5A93C', '#FF9F1C', '#4A0404', '#F8D7DA', '#1F1A1A'];

const AdminStatisticsPage = () => {
  const [overview, setOverview] = useState({});
  const [salesData, setSalesData] = useState({ data: [] });
  const [categorySales, setCategorySales] = useState([]);
  const [period, setPeriod] = useState('month');
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/admin/login'); return; }
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      const [overviewRes, salesRes, categoryRes] = await Promise.all([
        api.get('/statistics/overview'),
        api.get(`/statistics/sales?period=${period}`),
        api.get('/statistics/category-sales')
      ]);
      setOverview(overviewRes.data);
      setSalesData(salesRes.data);
      setCategorySales(categoryRes.data);
    } catch (error) { console.error('Error:', error); }
  };

  const yoyData = [
    { month: 'Jan', current: 18000, previous: 14000 }, { month: 'Feb', current: 22000, previous: 18000 },
    { month: 'Mar', current: 25000, previous: 20000 }, { month: 'Apr', current: 28000, previous: 22000 },
    { month: 'May', current: 30000, previous: 25000 }, { month: 'Jun', current: 35000, previous: 28000 }
  ];
  const forecastData = [{ month: 'Last', units: 120 }, { month: 'Current', units: 145 }, { month: '+1 mo', units: 160 }, { month: '+2 mo', units: 175 }, { month: '+3 mo', units: 180 }];
  const topProductsData = [{ name: 'Royal Red Banaras', sold: 45 }, { name: 'Mysore Silk', sold: 38 }, { name: 'Festive Lehenga', sold: 32 }, { name: 'Cotton Chudidhar', sold: 28 }, { name: 'Designer Gown', sold: 25 }];
  const acquisitionData = [{ month: 'Jan', customers: 12 }, { month: 'Feb', customers: 18 }, { month: 'Mar', customers: 25 }, { month: 'Apr', customers: 32 }, { month: 'May', customers: 40 }, { month: 'Jun', customers: 55 }];
  const funnelData = [{ stage: 'Catalogue Views', count: 5000 }, { stage: 'WhatsApp Clicks', count: 1200 }, { stage: 'Messages Sent', count: 800 }, { stage: 'Repeat Purchases', count: 250 }];
  const aovData = [{ month: 'Jan', aov: 1200 }, { month: 'Feb', aov: 1350 }, { month: 'Mar', aov: 1480 }, { month: 'Apr', aov: 1520 }, { month: 'May', aov: 1600 }, { month: 'Jun', aov: 1650 }];
  const genderData = [{ name: 'Female', value: 75 }, { name: 'Male (Gifts)', value: 20 }, { name: 'Other', value: 5 }];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center space-x-4">
          <Link to="/admin/dashboard" className="text-[#E5A93C]"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="font-heading text-xl font-bold text-[#2A0000]">Statistics Dashboard</h1>
        </div>
      </header>

      <div className="p-6 space-y-6">
        <div className="flex justify-end">
          <div className="flex space-x-2 bg-white rounded-lg p-1 border">
            {['today', 'week', 'month', 'year'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-2 text-sm rounded ${period === p ? 'bg-[#2A0000] text-white' : 'text-gray-600'}`} data-testid={`period-${p}`}>{p.charAt(0).toUpperCase() + p.slice(1)}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg border"><p className="text-sm text-gray-600">Total Revenue</p><p className="text-2xl font-bold text-[#E5A93C]">₹{salesData.total_revenue?.toLocaleString() || 0}</p></div>
          <div className="bg-white p-6 rounded-lg border"><p className="text-sm text-gray-600">Total Orders</p><p className="text-2xl font-bold text-[#2A0000]">{salesData.total_orders || 0}</p></div>
          <div className="bg-white p-6 rounded-lg border"><p className="text-sm text-gray-600">Avg Order Value</p><p className="text-2xl font-bold text-[#2A0000]">₹{salesData.average_order_value || 0}</p></div>
          <div className="bg-white p-6 rounded-lg border"><p className="text-sm text-gray-600">Total Customers</p><p className="text-2xl font-bold text-[#2A0000]">{overview.total_customers || 0}</p></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg border" data-testid="chart-sales-overview">
            <h3 className="font-bold text-[#2A0000] mb-4">Sales Overview</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={salesData.data || []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Bar dataKey="revenue" fill="#E5A93C" /></BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Year-on-Year Revenue</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={yoyData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Legend /><Line type="monotone" dataKey="current" stroke="#E5A93C" name="Current Year" /><Line type="monotone" dataKey="previous" stroke="#2A0000" name="Previous Year" /></LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Category Sales Mix</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart><Pie data={categorySales} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ category, value }) => `${category}: ${value}%`}>{categorySales.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Units Sold - Forecast</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={forecastData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Bar dataKey="units" fill="#FF9F1C" /></BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Top 10 Best-Selling Products</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topProductsData} layout="vertical"><CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis dataKey="name" type="category" width={100} /><Tooltip /><Bar dataKey="sold" fill="#E5A93C" /></BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Customer Acquisition</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={acquisitionData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Area type="monotone" dataKey="customers" stroke="#2A0000" fill="#E5A93C" fillOpacity={0.6} /></AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">WhatsApp Inquiry Funnel</h3>
            <div className="space-y-2">
              {funnelData.map((s, i) => {
                const w = (s.count / funnelData[0].count) * 100;
                return (
                  <div key={i} className="flex items-center">
                    <div className="w-32 text-sm">{s.stage}</div>
                    <div className="flex-1 bg-gray-100 rounded h-8 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-[#E5A93C] flex items-center justify-end pr-3 text-white text-xs font-bold" style={{ width: `${w}%` }}>{s.count.toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Average Order Value Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={aovData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Line type="monotone" dataKey="aov" stroke="#E5A93C" strokeWidth={3} /></LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Inventory Health Monitor</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between p-2 bg-green-50 rounded"><span>Banaras Sarees</span><span className="text-green-700 font-bold">Healthy (25)</span></div>
              <div className="flex justify-between p-2 bg-yellow-50 rounded"><span>Designer Gowns</span><span className="text-yellow-700 font-bold">Moderate (8)</span></div>
              <div className="flex justify-between p-2 bg-red-50 rounded"><span>Bridal Lehengas</span><span className="text-red-700 font-bold">Low (3)</span></div>
              <div className="flex justify-between p-2 bg-green-50 rounded"><span>Cotton Chudidhar</span><span className="text-green-700 font-bold">Healthy (18)</span></div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-[#2A0000] mb-4">Customer Gender Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart><Pie data={genderData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label>{genderData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie><Tooltip /><Legend /></PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg border lg:col-span-2">
            <h3 className="font-bold text-[#2A0000] mb-4">Festival/Season Revenue Heatmap</h3>
            <div className="grid grid-cols-12 gap-1">
              {Array.from({ length: 84 }, (_, i) => {
                const intensity = Math.random();
                const bg = intensity > 0.7 ? 'bg-[#2A0000]' : intensity > 0.5 ? 'bg-[#E5A93C]' : intensity > 0.3 ? 'bg-[#E5A93C]/50' : 'bg-gray-200';
                return <div key={i} className={`aspect-square ${bg} rounded`} />;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStatisticsPage;
